import { BehaviorSubject, Observable, ReplaySubject, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BaseService, getParsedError } from 'src/app/shared/services/base.service';
import { CredentialDefinitionModel } from '../../../shared/models/credential/credential-definition.model';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { ScenarioDefinitionModel } from './admin-credential-scenario/scenario-definition-model';
import {
  ICredentialFeeDefinitionModel,
  ICredentialFeeDefinitionWithIdModel,
  IScenarioRequirementBase,
  ScenarioRequirementDefinition,
} from 'src/app/shared/models/credential/scenarioRequirementDefinition.model';
import { EvidenceDefinition } from 'src/app/shared/models/credential/evidenceDefinition.model';
import {
  EvidenceAutomationActionType,
  RequirementAutomation,
} from './admin-credential-scenario/admin-credential-scenario-details/admin-requirement-side-bar/evidence-automation-condition-item';
import { CredentialApplicationAutoCloseRuleModel } from './admin-credential-application/admin-credential-application-auto-close-sidebar/credential-application-auto-close-rule.model';

import { CredentialEmailConfigurationModel } from 'src/app/shared/models/credential/credentialEmailConfiguration.model';
import {
  ActiveOrInactive,
  CredentialEmailModel,
} from 'src/app/shared/models/credential/credentialEmail.model';
import { CredentialApplicationDefinitionModel } from 'src/app/shared/models/credential/credential-application-definition.model';
import { CredentialApplicationDefinitionType } from 'src/app/shared/models/credential/CredentialApplicationDefinitionType';
import {
  CredentialSpecialtyDefinitionModel,
  CredentialSpecialtyDefinitionStatus,
} from 'src/app/admin/admin-home/admin-credentials/admin-credential-specialty/credential-specialty-definition.model';
import { AdminSpecialtySettingModel } from './admin-credential-application/admin-specialty-settings/admin-specialty-setting.model';
import { CredentialDefinitionUsageReport } from 'src/app/shared/models/credential/credentialDefinitionUsageReport';

@Injectable({
  providedIn: 'root',
})
export class AdminCredentialsService extends BaseService {
  private _credentials: CredentialDefinitionModel[] = [];
  private _scenarios: ScenarioDefinitionModel[] = [];
  private _specialties: CredentialSpecialtyDefinitionModel[] = [];
  private _scenariosFilterValue: string = 'All';
  public credentialFees: ReplaySubject<ICredentialFeeDefinitionWithIdModel[]> = new ReplaySubject<
    ICredentialFeeDefinitionWithIdModel[]
  >();

  public readonly credentials: BehaviorSubject<CredentialDefinitionModel[]> = new BehaviorSubject<
    CredentialDefinitionModel[]
  >(undefined);
  public readonly credential: BehaviorSubject<CredentialDefinitionModel> =
    new BehaviorSubject<CredentialDefinitionModel>(undefined);
  public readonly scenarios: BehaviorSubject<ScenarioDefinitionModel[]> = new BehaviorSubject<
    ScenarioDefinitionModel[]
  >([]);

  public readonly specialties: BehaviorSubject<CredentialSpecialtyDefinitionModel[]> =
    new BehaviorSubject<CredentialSpecialtyDefinitionModel[]>([]);

  public readonly scenario: BehaviorSubject<ScenarioDefinitionModel> =
    new BehaviorSubject<ScenarioDefinitionModel>(null);

  constructor() {
    super();
  }

  getAllCredentials(): Promise<CredentialDefinitionModel[]> {
    return this.get(this.credentialsBaseUrl, false, ApiVersions.v1_0)
      .pipe(map(this.convertCredentialsData))
      .toPromise()
      .then((result) => {
        this._credentials = result.sort((a, b) => a.name.localeCompare(b.name));
        this.credentials.next(this._credentials);
        return result;
      });
  }

  getCustomRules = (): Promise<string[]> =>
    this.get(`${this.credentialsBaseUrl}/custom-rules`, true, ApiVersions.v1_0).toPromise();

  getCredential(credentialId: string): void {
    let path = this.credentialsBaseUrl.concat('/', credentialId);

    this.get(path, true, ApiVersions.v1_0)
      .pipe(map((data) => new CredentialDefinitionModel(data)))
      .subscribe((result) => {
        this.credential.next(result);
        this._scenarios = result.scenarios;
        this.scenarios.next(this._scenarios);
      });
  }

  getUsageReport = async (
    credential: CredentialDefinitionModel
  ): Promise<CredentialDefinitionUsageReport> => {
    return await firstValueFrom(
      this.get<CredentialDefinitionUsageReport>(
        `${this.credentialsBaseUrl}/${credential.id}/usage`,
        true,
        ApiVersions.v0_1
      ).pipe(map((result) => new CredentialDefinitionUsageReport(result)))
    );
  };

  createCredential = (value: CredentialDefinitionModel): Promise<CredentialDefinitionModel> =>
    this.post(this.credentialsBaseUrl, value, null, ApiVersions.v1_0)
      .toPromise()
      .then((res) => {
        if (!res) {
          return null;
        }
        const credential = new CredentialDefinitionModel(res);
        this._credentials.push(credential);
        this.credentials.next(this._credentials);
        this.openSnackBar(`${value.name} was created successfully`, true);
        this.router.navigate(['/admin/credentials', credential.id]);
        return credential;
      });

  updateCredential = (credential: CredentialDefinitionModel) =>
    this.put(
      `${this.credentialsBaseUrl}/${credential.id}`,
      JSON.stringify(credential),
      null,
      ApiVersions.v1_0
    ).pipe(
      map((res) => {
        if (res) {
          let credential = new CredentialDefinitionModel(res);
          let index = this._credentials.findIndex((x) => x.id === credential.id);
          this._credentials[index] = credential;
          this.credentials.next(this._credentials);
          this.credential.next(credential);
          this.openSnackBar('Credential updated successfully', true);
          return true;
        }
        return false;
      })
    );

  archiveCredential = (credential: CredentialDefinitionModel): Observable<boolean> =>
    this.post(
      `${this.credentialsBaseUrl}/${credential.id}/archive`,
      null,
      null,
      ApiVersions.v0_1
    ).pipe(
      map((res) => {
        if (res) {
          let credential = new CredentialDefinitionModel(res);
          let index = this._credentials.findIndex((x) => x.id === credential.id);
          this._credentials[index] = credential;
          this.credentials.next(this._credentials);
          this.credential.next(credential);
          this.openSnackBar('Credential archived successfully', true);
          return true;
        }
        return false;
      })
    );

  deleteCredential(credential: CredentialDefinitionModel): void {
    let path = this.credentialsBaseUrl.concat('/', credential.id);
    this.delete(path, null, ApiVersions.v1_0).subscribe(() => {
      this._credentials = this._credentials.filter((x) => x.id !== credential.id);
      this.credentials.next(this._credentials);
      this.openSnackBar(`${credential.name} was deleted successfully`, true);
    });
  }

  getCredentialApplication(
    credentialApplicationId: string
  ): Promise<CredentialApplicationDefinitionModel> {
    let path = `credential-applications/${credentialApplicationId}`;

    return this.get(path, true, ApiVersions.v1_0)
      .pipe(map((data) => new CredentialApplicationDefinitionModel(data)))
      .toPromise();
  }

  addCredentialApplication(
    credentialId: string,
    model: any,
    type: CredentialApplicationDefinitionType
  ): Promise<any> {
    let path =
      type == CredentialApplicationDefinitionType.Initial
        ? this.credentialsBaseUrl.concat('/', credentialId, '/applications/initial')
        : type == CredentialApplicationDefinitionType.Renewal
        ? this.credentialsBaseUrl.concat('/', credentialId, '/applications/renewal')
        : type == CredentialApplicationDefinitionType.Reinstatement
        ? this.credentialsBaseUrl.concat('/', credentialId, '/applications/reinstatement')
        : this.credentialsBaseUrl.concat('/', credentialId, '/applications/specialty');
    return this.post(path, model, null, ApiVersions.v1_0)
      .pipe(
        map((data) => {
          if (data) {
            let credential = new CredentialDefinitionModel(data);
            let index = this._credentials.findIndex((x) => x.id === credential.id);
            this._credentials[index] = credential;
            this.credentials.next(this._credentials);
            this.credential.next(credential);
            this.openSnackBar(`${type} application was added successfully`, true);

            this.router.navigate([
              '/admin/credentials',
              credential.id,
              'application',
              credential.credentialApplications[type].id,
            ]);
            return true;
          }
        })
      )
      .toPromise();
  }

  getScenario = (credentialId: string, scenarioId: string): Promise<ScenarioDefinitionModel> =>
    this.get(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}`,
      false,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((data) => {
        const scenario = new ScenarioDefinitionModel(data);
        this.scenario.next(scenario);
        return scenario;
      });

  addScenario(credentialId: string, value: ScenarioDefinitionModel): void {
    let path = this.credentialsBaseUrl.concat('/', credentialId, '/scenarios');
    this.post(path, value, null, ApiVersions.v1_0).subscribe((res) => {
      if (res) {
        let scenario = new ScenarioDefinitionModel(res);
        this._scenarios.push(scenario);
        this.scenarios.next(this._scenarios);
        this.openSnackBar(`${value.name} was added successfully`, true);
        this.router.navigate(['/admin/credentials', credentialId, 'scenarios', scenario.id]);
      }
    });
  }

  addScenarioToApplication(credentialApplicationId: string, value: ScenarioDefinitionModel): void {
    let path = `credential-applications/${credentialApplicationId}/scenarios`;
    this.post(path, value, null, ApiVersions.v1_0).subscribe((res) => {
      if (res) {
        let scenario = new ScenarioDefinitionModel(res);
        if (this._scenarios.findIndex((x) => x.id === scenario.id) == -1) {
          this._scenarios.push(scenario);
          this.scenarios.next(this._scenarios);
        }
        var credential = this.credential.value;
        const applicationDefinition = credential.findApplicationById(credentialApplicationId);

        applicationDefinition.scenarios.push(scenario);

        this.credential.next(credential);
        this.router.navigate(['/admin/credentials', credential.id, 'scenarios', scenario.id], {
          queryParams: { appId: credentialApplicationId },
        });
        this.openSnackBar(`${value.name} was added successfully`, true);
      }
    });
  }

  removeScenarioFromApplication = (credentialApplicationId: string, scenarioId: string) =>
    this.delete(
      `credential-applications/${credentialApplicationId}/scenarios/${scenarioId}`,
      null,
      ApiVersions.v1_0
    )
      .pipe(first())
      .subscribe((result) => {
        if (!result) return;
        this._scenarios = this._scenarios.filter((x) => x.id !== scenarioId);
        var credential = this.credential.value;
        const applicationDefinition = credential.findApplicationById(credentialApplicationId);
        applicationDefinition.scenarios = applicationDefinition.scenarios.filter(
          (x) => x.id !== scenarioId
        );
        this.credential.next(credential);
        this.openSnackBar('Scenario removed successfully', true);
      });

  updateScenario = (
    credentialId: string,
    scenario: ScenarioDefinitionModel
  ): Promise<ScenarioDefinitionModel> =>
    this.put(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenario.id}/`,
      JSON.stringify(scenario),
      null,
      ApiVersions.v1_0,
      true
    )
      .toPromise()
      .then((res) => {
        const scenario = new ScenarioDefinitionModel(res);
        let index = this._scenarios.findIndex((x) => x.id === scenario.id);
        this._scenarios[index] = scenario;
        this.filterScenarios(this._scenariosFilterValue);
        let credential = this.credential.value;
        credential.scenarios = this._scenarios;
        credential.updateAllApplications((applicationDefinition) => {
          const appScenario = applicationDefinition.scenarios.find((x) => x.id === scenario.id);
          if (appScenario) appScenario.status = scenario.status;
        });
        this.credential.next(credential);
        this.scenario.next(scenario);
        return scenario;
      });

  deleteScenario(credentialId: string, scenario: ScenarioDefinitionModel): void {
    let path = this.credentialsBaseUrl.concat('/', credentialId, '/scenarios/', scenario.id);
    this.delete(path, null, ApiVersions.v1_0, true).subscribe(
      () => {
        this._scenarios = this._scenarios.filter((x) => x.id !== scenario.id);
        this.filterScenarios(this._scenariosFilterValue);
        let credential = this.credential.value;
        credential.scenarios = this._scenarios;
        credential.updateAllApplications((applicationDefinition) => {
          applicationDefinition.scenarios = applicationDefinition.scenarios.filter(
            (x) => x.id !== scenario.id
          );
        });
        this.openSnackBar(`${scenario.name} was deleted successfully`, true);
        this.router.navigate(['/admin/credentials', credentialId]);
      },
      (error) => {
        this.openSnackBar(error.error, false);
      }
    );
  }

  cloneScenario(credentialId: string, scenarioId: string): void {
    let path = this.credentialsBaseUrl.concat(
      '/',
      credentialId,
      '/scenarios/',
      scenarioId,
      '/clone'
    );
    this.post(path, null, null, ApiVersions.v1_0, true).subscribe(
      () => {
        this.openSnackBar(`Scenario was cloned successfully`, true);
        this.getCredential(credentialId);
      },
      (error) => {
        this.openSnackBar(error.error, false);
      }
    );
  }

  filterScenarios(filterValue: string): void {
    this._scenariosFilterValue = filterValue;
    if (filterValue === 'All') {
      this.scenarios.next(this._scenarios);
      return;
    }
    let scenarios = this._scenarios.filter((x) => x.status === filterValue);
    this.scenarios.next(scenarios);
  }

  resetScenariosFilterValue(): void {
    this._scenariosFilterValue = 'All';
  }

  getCredentialFee = (
    credentialId: string,
    credentialFeeDefinitionId: string
  ): Promise<ICredentialFeeDefinitionModel> =>
    this.get(
      `${this.credentialsBaseUrl}/${credentialId}/fees/${credentialFeeDefinitionId}`,
      null,
      ApiVersions.v1_0
    ).toPromise();

  deleteCredentialFee = async (
    credentialId: string,
    credentialFeeDefinitionId: string
  ): Promise<boolean> => {
    const isSuccess = await this.delete(
      `${this.credentialsBaseUrl}/${credentialId}/fees/${credentialFeeDefinitionId}`,
      null,
      ApiVersions.v1_0
    ).toPromise();
    if (isSuccess) {
      await this.getCredentialFees(credentialId);
    }
    return isSuccess;
  };

  getCredentialFees = async (
    credentialId: string,
    excludeFeesFromScenarioId: string = null
  ): Promise<Observable<ICredentialFeeDefinitionWithIdModel[]>> => {
    const fees = await this.get(
      `${this.credentialsBaseUrl}/${credentialId}/fees`,
      excludeFeesFromScenarioId
        ? {
            params: { excludeFeesFromScenarioId },
          }
        : null,
      ApiVersions.v1_0
    ).toPromise();
    this.credentialFees.next(fees);
    return this.credentialFees;
  };

  updateCredentialFee = (
    credentialId: string,
    credentialFeeDefinitionId: string,
    model: ICredentialFeeDefinitionModel
  ): Promise<ICredentialFeeDefinitionModel> =>
    this.put(
      `${this.credentialsBaseUrl}/${credentialId}/fees/${credentialFeeDefinitionId}`,
      JSON.stringify(model),
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then(async (fee) => {
        await this.getCredentialFees(credentialId);
        return fee;
      });

  createCredentialFee = (
    credentialId: string,
    model: ICredentialFeeDefinitionModel
  ): Promise<ICredentialFeeDefinitionModel> =>
    this.post(
      `${this.credentialsBaseUrl}/${credentialId}/fees`,
      JSON.stringify(model),
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then(async (fee) => {
        await this.getCredentialFees(credentialId);
        return fee;
      });

  deleteRequirement = async (
    credentialId: string,
    scenarioId: string,
    requirementId: string
  ): Promise<boolean> =>
    this.delete(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}`,
      null,
      ApiVersions.v1_0
    ).toPromise();

  /** note: parseRequirementResult will refresh the scenario after creating. no need to add new requirement to scenario.requirements */
  createRequirement(
    credentialId: string,
    scenarioId: string,
    value: IScenarioRequirementBase & { credentialFeeDefinitionId?: string }
  ): Promise<ScenarioRequirementDefinition> {
    return this.post(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements`,
      value,
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((jsonResult) => this.parseRequirementResult(jsonResult, credentialId, scenarioId));
  }

  updateRequirement(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    value: IScenarioRequirementBase
  ): Promise<ScenarioRequirementDefinition> {
    return this.put(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements`,
      JSON.stringify({
        ...value,
        id: requirementId,
      }),
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((jsonResult) => this.parseRequirementResult(jsonResult, credentialId, scenarioId));
  }

  private parseRequirementResult = async (jsonResult, credentialId: string, scenarioId: string) => {
    if (!jsonResult) {
      return; //API error
    }
    //a hack trigger 'this.scenario.next(scenario)' with a refreshed scenario;
    //without this; editing requirement name or subscription won't show up without page refreshing
    //alternatively we could keep track of the scenario model, edit it then call this.scenario.next(scenario) without an API call
    await this.getScenario(credentialId, scenarioId);

    return ScenarioRequirementDefinition.parse(jsonResult);
  };

  addOrUpdateRequirementAutomation(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    value: RequirementAutomation
  ): Promise<RequirementAutomation[]> {
    return this.post(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/automations`,
      JSON.stringify(value),
      null,
      ApiVersions.v1_0
    )
      .pipe(map((jsonResult) => (jsonResult ? (jsonResult as RequirementAutomation[]) : null)))
      .toPromise();
  }

  deleteRequirementAutomation(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    automation: RequirementAutomation
  ): Promise<boolean> {
    return this.delete(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/automations/${automation.id}`,
      null,
      ApiVersions.v1_0,
      true
    )
      .pipe(
        map(() => {
          return true;
        }),
        catchError((error) => {
          this.openSnackBar(error.error, false);
          return of(false);
        })
      )
      .toPromise();
  }

  getRequirementAutomations = (
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    automationActionType: EvidenceAutomationActionType
  ): Promise<RequirementAutomation[]> =>
    this.get(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/automations/${automationActionType}`,
      null,
      ApiVersions.v1_0
    ).toPromise();

  createEvidence(
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    value: EvidenceDefinition
  ): Promise<EvidenceDefinition> {
    return this.post(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/`,
      value,
      null,
      ApiVersions.v1_0
    ).toPromise();
  }

  deleteEvidence = async (
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    evidenceId
  ): Promise<boolean> => {
    try {
      await this.delete(
        `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/${evidenceId}`,
        null,
        ApiVersions.v1_0,
        true
      ).toPromise();
      return true;
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);
      return false;
    }
  };

  getEvidences = (
    credentialId: string,
    scenarioId: string,
    requirementId: string
  ): Promise<{
    evidences: EvidenceDefinition[];
    credentialFeeDefinition: ICredentialFeeDefinitionModel;
  }> =>
    this.get(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/`,
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((result) => {
        if (result.baseAmount) {
          return {
            evidences: null,
            credentialFeeDefinition: result as ICredentialFeeDefinitionModel,
          };
        }
        return {
          evidences: result as EvidenceDefinition[],
          credentialFeeDefinition: null,
        };
      });

  createSpecialtyEvidence(
    credentialId: string,
    value: EvidenceDefinition
  ): Promise<EvidenceDefinition> {
    return this.post(
      `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/`,
      value,
      null,
      ApiVersions.v1_0
    ).toPromise();
  }

  editSpecialtyEvidence(
    credentialId: string,
    value: EvidenceDefinition
  ): Promise<EvidenceDefinition> {
    return this.put(
      `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/${value.id}/`,
      JSON.stringify(value),
      null,
      ApiVersions.v1_0
    ).toPromise();
  }

  async deleteSpecialtyEvidence(credentialId: string, evidenceId: string): Promise<boolean> {
    try {
      await this.delete(
        `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/${evidenceId}/`,
        null,
        ApiVersions.v1_0,
        true
      ).toPromise();

      return true;
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);

      return false;
    }
  }

  getSpecialtyEvidences = (credentialId: string): Promise<EvidenceDefinition[]> =>
    this.get(
      `${this.credentialsBaseUrl}/${credentialId}/specialties/evidences/`,
      null,
      ApiVersions.v1_0
    )
      .toPromise()
      .then((result) => {
        return result as EvidenceDefinition[];
      });

  updateCredentialApplicationAutoCloseRule(
    application: CredentialApplicationDefinitionModel,
    value: CredentialApplicationAutoCloseRuleModel
  ): Promise<CredentialApplicationAutoCloseRuleModel> {
    const path = `credential-applications/${application.id}/auto-close-rule`;
    const body = JSON.stringify(value);
    return this.post(path, body, null, ApiVersions.v1_0).toPromise();
  }

  deleteCredentialApplicationAutoCloseRule(
    application: CredentialApplicationDefinitionModel
  ): Promise<boolean> {
    const path = `credential-applications/${application.id}/auto-close-rule`;
    return this.delete(path, null, ApiVersions.v1_0, true)
      .pipe(
        map(() => {
          return true;
        }),
        catchError((error) => {
          this.openSnackBar(error.error, false);
          return of(false);
        })
      )
      .toPromise();
  }

  rankEvidences = (
    credentialId: string,
    scenarioId: string,
    requirementId: string,
    evidenceOrder: string[]
  ): Promise<boolean> =>
    this.patch(
      `${this.credentialsBaseUrl}/${credentialId}/scenarios/${scenarioId}/requirements/${requirementId}/evidences/rank`,
      JSON.stringify(evidenceOrder),
      ApiVersions.v1_0
    )
      .pipe(map((jsonResult) => !!jsonResult)) //return will be null on error, true on success
      .toPromise();

  getCredentialEmailConfig = (id: string): Promise<CredentialEmailConfigurationModel> =>
    this.get(`${this.credentialsBaseUrl}/${id}/emails`, true, ApiVersions.v1_0)
      .pipe(map((data) => CredentialEmailConfigurationModel.parse(data)))
      .toPromise();

  /** returns null on error */
  addOrUpdateCredentialEmail = async (
    credentialId: string,
    email: CredentialEmailModel
  ): Promise<CredentialEmailModel> => {
    try {
      return await this.post(
        `${this.credentialsBaseUrl}/${credentialId}/emails`,
        JSON.stringify(email),
        null,
        ApiVersions.v0_1,
        true
      ).toPromise();
    } catch (error) {
      this.openSnackBar(getParsedError(error).message, false);
      return null;
    }
  };

  updateCredentialEmailStatus(
    credentialId: string,
    credentialEmailId: string,
    newStatus: ActiveOrInactive
  ): Promise<CredentialEmailModel> {
    return this.patch(
      `${this.credentialsBaseUrl}/${credentialId}/emails/${credentialEmailId}`,
      JSON.stringify(newStatus),
      ApiVersions.v0_1
    )
      .pipe(map((jsonResult) => (jsonResult ? (jsonResult as CredentialEmailModel) : null)))
      .toPromise();
  }

  addCredentialSpecialty(
    credentialDefinitionId: string,
    credentialSpecialtyDefinition: CredentialSpecialtyDefinitionModel
  ) {
    let path = `credential-specialties/${credentialDefinitionId}`;

    this.post(
      `${path}`,
      JSON.stringify(credentialSpecialtyDefinition),
      null,
      ApiVersions.v1_0,
      true
    ).subscribe((res) => {
      if (res) {
        let credentialSpecialty = new CredentialSpecialtyDefinitionModel(res);
        this._specialties.push(credentialSpecialty);
        this.specialties.next(this._specialties);
        this.openSnackBar(`${credentialSpecialtyDefinition.name} was added successfully`, true);
      }
    });
  }

  updateSpecialty(
    credentialDefinitionId: string,
    credentialSpecialtyDefinitionId: string,
    credentialSpecialtyDefinitionModel: CredentialSpecialtyDefinitionModel
  ) {
    let path = `credential-specialties/${credentialDefinitionId}/${credentialSpecialtyDefinitionId}`;

    const body = JSON.stringify(credentialSpecialtyDefinitionModel);

    this.put(path, body, null, ApiVersions.v1_0).subscribe(
      (res) => {
        if (res) {
          let credentialSpecialty = new CredentialSpecialtyDefinitionModel(res);
          let index = this._specialties.findIndex((x) => x.id === credentialSpecialty.id);
          this._specialties[index] = credentialSpecialty;
          this.specialties.next(this._specialties);
          this.openSnackBar(
            `${credentialSpecialtyDefinitionModel.name} was updated successfully`,
            true
          );
        }
      },
      (error) => {
        this.openSnackBar(getParsedError(error).message, false);
      }
    );
  }

  deleteSpecialty(credentialDefinitionId: string, credentialSpecialtyDefinitionId: string) {
    this.delete(
      `credential-specialties/${credentialDefinitionId}/${credentialSpecialtyDefinitionId}`,
      null,
      ApiVersions.v1_0,
      true
    ).subscribe(() => {
      this._specialties = this._specialties.filter((x) => x.id !== credentialSpecialtyDefinitionId);
      this.specialties.next(this._specialties);
      this.openSnackBar(`Specialty was deleted successfully`, true);
    });
  }

  getSpecialties(credentialDefinitionId: string): void {
    this.get(`credential-specialties/${credentialDefinitionId}`, null, ApiVersions.v1_0).subscribe(
      (result) => {
        this._specialties = result;
        this.specialties.next(this._specialties);
      }
    );
  }

  updateSpecialtyStatus(
    credentialDefinitionId: string,
    credentialSpecialtyDefinitionId: string,
    CredentialSpecialtyDefinitionStatus: CredentialSpecialtyDefinitionStatus
  ) {
    let path = `credential-specialties/${credentialDefinitionId}/${credentialSpecialtyDefinitionId}`;
    const body = JSON.stringify(CredentialSpecialtyDefinitionStatus);
    this.patch(path, body, ApiVersions.v1_0).subscribe((res) => {
      if (res) {
        let credentialSpecialty = new CredentialSpecialtyDefinitionModel(res);
        let index = this._specialties.findIndex((x) => x.id === credentialSpecialty.id);
        this._specialties[index] = credentialSpecialty;
        this.specialties.next(this._specialties);
        this.openSnackBar(`Status was updated successfully`, true);
      }
    });
  }

  addOrUpdateSpecialtySetting(
    applicationId: string,
    value: AdminSpecialtySettingModel
  ): Promise<AdminSpecialtySettingModel> {
    const path = `credential-applications/${applicationId}/specialty-setting`;
    const body = JSON.stringify(value);
    return this.post(path, body, null, ApiVersions.v1_0).toPromise();
  }

  private get credentialsBaseUrl(): string {
    return 'credential-definition';
  }

  private convertCredentialsData(data: CredentialDefinitionModel[]) {
    if (!data) {
      return [];
    }
    return data.map((credential) => new CredentialDefinitionModel(credential));
  }
}
function firstValueFrom(
  arg0: Observable<CredentialDefinitionUsageReport>
): CredentialDefinitionUsageReport | PromiseLike<CredentialDefinitionUsageReport> {
  throw new Error('Function not implemented.');
}
